import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Route } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { getVtmplvDisplayName } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/utils';
import { VerificationTemplateVersionDiff } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import VerificationTemplateEditor, {
  VerificationTemplateEditorFormData,
} from 'dashboard/apps/VerificationTemplateEditor';
import useIsEditableVersion from 'dashboard/apps/VerificationTemplateEditor/hooks/useIsEditableVersion';
import VerificationTemplateDiff from 'dashboard/apps/VerificationTemplateEditor/VerificationTemplateDiff';
import VerificationTemplateHeader, {
  getPendingStatesTitle,
  PendingStates,
} from 'dashboard/apps/VerificationTemplateEditor/VerificationTemplateHeader';
import LoadingModal from 'dashboard/components/LoadingModal';
import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import { MatchRequirementProfile } from 'dashboard/models/match-requirement-profile';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import {
  VerificationTemplateVersion,
  VerificationTemplateVersionSchema,
} from 'dashboard/models/verification-template-version';
import useToggle from 'lib/hooks/useToggle';

const VerificationTemplateEditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  overflow: hidden;
`;

interface DashboardVerificationTemplateShowViewProps {
  verificationTemplate: VerificationTemplate | undefined;
  verificationTemplateVersion: VerificationTemplateVersion | undefined;
  latestPublishedVerificationTemplateVersion: VerificationTemplateVersion | undefined;
  verificationTemplateVersionDiff: VerificationTemplateVersionDiff | undefined;
  matchRequirementProfiles: MatchRequirementProfile[] | undefined;
  schema: VerificationTemplateVersionSchema | undefined;
  pendingStates: PendingStates;
  isFormPristine: boolean;
  isFormValid: boolean;
  handleDiscardDraft: (verificationTemplateId: string) => void;
  handlePublishDraft: () => void;
  handleSave: (verificationTemplateVersion: VerificationTemplateVersion) => void;
  handleLoadVersionDiff: () => void;
  handleLoadLatestPublishedVersion: () => void;
  submit: () => void;
}

const DashboardVerificationTemplateShowView = ({
  verificationTemplate,
  verificationTemplateVersion,
  latestPublishedVerificationTemplateVersion,
  verificationTemplateVersionDiff,
  matchRequirementProfiles,
  schema,
  pendingStates,
  isFormPristine,
  isFormValid,
  handleDiscardDraft,
  handlePublishDraft,
  handleSave,
  handleLoadVersionDiff,
  handleLoadLatestPublishedVersion,
  submit,
}: DashboardVerificationTemplateShowViewProps) => {
  const editable = useIsEditableVersion();
  const { isLoading } = pendingStates;
  const [showJsonDiff, { set: setJsonDiff }] = useToggle(false);
  const initialValues = useMemo<VerificationTemplateEditorFormData>(
    () => ({
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      verificationTemplate: verificationTemplate,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      verificationTemplateVersion: verificationTemplateVersion,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      matchRequirementProfiles: matchRequirementProfiles,
      schema: schema,
    }),
    [matchRequirementProfiles, schema, verificationTemplate, verificationTemplateVersion],
  );

  if (verificationTemplate == null || verificationTemplateVersion == null) {
    return isLoading ? (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    ) : (
      <Backdrop>
        <NullState
          message='We couldn’t find that Verification Template.'
          backRoute='/dashboard/verification-templates'
        />
      </Backdrop>
    );
  }

  const displayName = getVtmplvDisplayName(verificationTemplateVersion);
  const isProcessing = Object.values(pendingStates).some((pending) => pending);

  const submitVerificationTemplateForm = async (
    formData: Readonly<VerificationTemplateEditorFormData>,
  ) => {
    const { verificationTemplateVersion } = formData;
    await handleSave(verificationTemplateVersion);
  };

  return (
    <>
      <Helmet title={`${displayName} - Templates - Verifications`} />
      <LoadingModal title={getPendingStatesTitle(pendingStates)} isLoading={isProcessing} />
      <VerificationTemplateEditorContainer>
        <VerificationTemplateHeader
          verificationTemplate={verificationTemplate}
          verificationTemplateVersion={verificationTemplateVersion}
          relatedInquiryTemplateVersions={schema?.['inquiry-template-versions']?.data || []}
          pendingStates={pendingStates}
          isFormPristine={isFormPristine}
          isFormValid={isFormValid}
          displayEditingActions={editable}
          showJsonDiff={showJsonDiff}
          handleDiscardDraft={handleDiscardDraft}
          handlePublishDraft={handlePublishDraft}
          handleSave={submit}
          setJsonDiff={setJsonDiff}
        />

        <SentryRoutes>
          <Route
            index
            element={
              <VerificationTemplateEditor
                editable={editable}
                isFormPristine={isFormPristine}
                initialValues={initialValues}
                onSubmit={submitVerificationTemplateForm}
                hasInquiryTemplate={(schema?.['inquiry-template-versions']?.data || []).length > 0}
              />
            }
          />
          <Route
            path='review-changes'
            element={
              <VerificationTemplateDiff
                verificationTemplateVersion={verificationTemplateVersion}
                latestPublishedVerificationTemplateVersion={
                  latestPublishedVerificationTemplateVersion
                }
                verificationTemplateVersionDiff={verificationTemplateVersionDiff}
                showJsonDiff={showJsonDiff}
                handleLoadVersionDiff={handleLoadVersionDiff}
                handleLoadLatestPublishedVersion={handleLoadLatestPublishedVersion}
              />
            }
          />
        </SentryRoutes>
      </VerificationTemplateEditorContainer>
    </>
  );
};

export default DashboardVerificationTemplateShowView;
